import { AfterViewInit, Component, computed, effect, ElementRef, inject, input, output, signal, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ReferralMessage } from '@xmedical/core';

export interface MessageDisplay extends ReferralMessage {
  isSent: boolean;
  senderName: string;
}

@Component({
  selector: 'xm-referral-messages',
  standalone: true,
  imports: [
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule
],
  providers: [DatePipe],
  templateUrl: './referral-messages.component.html',
  styleUrl: './referral-messages.component.scss'
})
export class ReferralMessagesComponent implements AfterViewInit {
  private datePipe = inject(DatePipe);

  @ViewChild('messagesContainer') messagesContainer!: ElementRef<HTMLDivElement>;

  // Inputs
  messages = input<ReferralMessage[]>([]);
  isSending = input(false);
  disabled = input(false);
  currentPracticeId = input.required<number>();

  // Outputs
  messageSend = output<string>();

  // Local state
  newMessage = signal('');
  private viewInitialized = false;

  constructor() {
    // Scroll to bottom when messages change
    effect(() => {
      // Track the messages signal
      this.messages();
      // Only scroll after view is initialized
      if (this.viewInitialized) {
        this.scrollToBottom();
      }
    });
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.scrollToBottom();
  }

  private scrollToBottom(): void {
    // Use setTimeout to ensure DOM has updated
    setTimeout(() => {
      if (this.messagesContainer?.nativeElement) {
        const container = this.messagesContainer.nativeElement;
        container.scrollTop = container.scrollHeight;
      }
    });
  }

  // Computed: Messages with sent/received flag, sorted by date
  displayMessages = computed(() => {
    const msgs = this.messages();
    const practiceId = this.currentPracticeId();

    return [...msgs]
      .sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime())
      .map(msg => ({
        ...msg,
        isSent: msg.practiceId === practiceId,
        senderName: msg.createdUser?.name || 'Unknown'
      } as MessageDisplay));
  });

  // Computed: Group messages by date for date headers
  groupedMessages = computed(() => {
    const messages = this.displayMessages();
    const groups: { date: string; messages: MessageDisplay[] }[] = [];

    let currentDateStr = '';
    let currentGroup: MessageDisplay[] = [];

    for (const msg of messages) {
      const dateStr = this.formatDate(msg.createdDate);
      if (dateStr !== currentDateStr) {
        if (currentGroup.length > 0) {
          groups.push({ date: currentDateStr, messages: currentGroup });
        }
        currentDateStr = dateStr;
        currentGroup = [msg];
      } else {
        currentGroup.push(msg);
      }
    }

    if (currentGroup.length > 0) {
      groups.push({ date: currentDateStr, messages: currentGroup });
    }

    return groups;
  });

  onMessageInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.newMessage.set(value);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  sendMessage(): void {
    const message = this.newMessage().trim();
    if (!message || this.isSending() || this.disabled()) return;

    this.messageSend.emit(message);
    this.newMessage.set('');
  }

  formatTime(date: Date | string): string {
    if (!date) return '';
    return this.datePipe.transform(date, 'HH:mm') || '';
  }

  formatDate(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (this.isSameDay(d, today)) {
      return 'Today';
    } else if (this.isSameDay(d, yesterday)) {
      return 'Yesterday';
    }
    return this.datePipe.transform(date, 'dd MMM yyyy') || '';
  }

  private isSameDay(d1: Date, d2: Date): boolean {
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  }
}
